import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import microbiologyService from '../../services/microbiologyService';
import WorkbenchHeader from './components/WorkbenchHeader';
import PatientDetailsDrawer from './components/PatientDetailsDrawer';
import WorkflowProgressBar from './components/WorkflowProgressBar';
import WorkbenchBottomBar from './components/WorkbenchBottomBar';
import MicroscopySection from './components/MicroscopySection';
import CultureSection from './components/CultureSection';
import OrganismASTSection from './components/OrganismASTSection';
import ResistanceExpertRulesSection from './components/ResistanceExpertRulesSection';
import CommentsLibrarySection from './components/CommentsLibrarySection';
import PatientHistorySection from './components/PatientHistorySection';
import DoctorReviewModeSection from './components/DoctorReviewModeSection';
import WorkbenchSidebar from './components/WorkbenchSidebar';
import MicrobiologyAIModal from './components/MicrobiologyAIModal';
import MicrobiologyReportModal from './MicrobiologyReportModal';
import { checkReportReadiness } from './utils/microbiologyTemplateEngine';
import './MicrobiologyWorkbench.css';

export default function MicrobiologyWorkbenchPage() {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('microscopy'); // 'microscopy' | 'culture' | 'ast' | 'resistance' | 'comments' | 'history' | 'validation'
  const [activeIsolateIdx, setActiveIsolateIdx] = useState(0);

  // Auto-save & sync status
  const [saveState, setSaveState] = useState('saved'); // 'saved' | 'saving' | 'unsaved'
  const [lastSavedTime, setLastSavedTime] = useState('');
  const [isSnapshotCollapsed, setIsSnapshotCollapsed] = useState(false);

  // Modals & Drawers
  const [patientDrawerOpen, setPatientDrawerOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  // Clinical Override Modal
  const [overrideModal, setOverrideModal] = useState({
    isOpen: false,
    astResultId: null,
    drugName: '',
    originalInterpretation: '',
    newInterpretation: 'R',
    reason: ''
  });

  // Master Data Lookups
  const [organisms, setOrganisms] = useState([]);
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [resistanceMechanisms, setResistanceMechanisms] = useState([]);
  const [commentTemplates, setCommentTemplates] = useState([]);

  // Load Workbench & Masters
  const loadWorkbench = useCallback(async () => {
    try {
      setLoading(true);
      const [wbRes, orgRes, drugRes, resRes, cRes] = await Promise.all([
        microbiologyService.getWorkbenchDetail(testId),
        microbiologyService.getOrganisms(),
        microbiologyService.getAntimicrobials(),
        microbiologyService.getResistanceMechanisms(),
        microbiologyService.getCommentTemplates()
      ]);

      setData(wbRes?.data || null);
      setOrganisms(orgRes?.data || []);
      setAntimicrobials(drugRes?.data || []);
      setResistanceMechanisms(resRes?.data || []);
      setCommentTemplates(cRes?.data || []);
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setSaveState('saved');
    } catch (err) {
      console.error('Error loading culture workbench:', err);
      alert('Failed to load culture workbench data.');
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    loadWorkbench();
  }, [loadWorkbench]);

  const currentIsolate = data?.isolates?.[activeIsolateIdx] || null;

  // Handlers: Save Microscopy
  const handleSaveMicroscopy = async (payload) => {
    try {
      setSaveState('saving');
      await microbiologyService.saveMicroscopy(payload);
      setData((prev) => ({
        ...prev,
        gramStain: payload.gramStain,
        pusCells: payload.pusCells,
        epithelialCells: payload.epithelialCells,
        rbc: payload.rbc,
        microscopyObservations: payload.gramStain
      }));
      setSaveState('saved');
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      console.error('Error saving microscopy:', err);
      setSaveState('unsaved');
      alert('Failed to save microscopy observations.');
    }
  };

  // Handlers: Save Culture Status & Incubation
  const handleSaveCultureStatus = async (status, colonyCount, significance, critical, mediaPlates) => {
    try {
      setSaveState('saving');
      await microbiologyService.saveCultureStatus({
        cultureID: data.cultureID,
        cultureStatus: status,
        colonyCount: colonyCount,
        growthSignificance: significance,
        isCriticalAlert: critical,
        mediaPlates: mediaPlates
      });
      setData((prev) => ({
        ...prev,
        cultureStatus: status,
        colonyCount: colonyCount,
        growthSignificance: significance,
        isCriticalAlert: critical,
        ...(mediaPlates ? { mediaPlates } : {})
      }));
      setSaveState('saved');
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      console.error('Error saving culture status:', err);
      setSaveState('unsaved');
      alert('Failed to update culture status.');
    }
  };

  // Handlers: Add Isolate
  const handleAddIsolate = async () => {
    const defaultOrg = organisms[0]?.organismID || 1;
    try {
      setSaveState('saving');
      await microbiologyService.saveIsolate({
        cultureID: data.cultureID,
        organismID: defaultOrg,
        colonyMorphology: 'Smooth colonies observed',
        quantityDescription: 'Heavy Growth (> 10^5 CFU/mL)',
        identificationMethod: 'Automated VITEK-2 Compact System'
      });
      await loadWorkbench();
      if (data?.isolates) {
        setActiveIsolateIdx(data.isolates.length);
      }
    } catch (err) {
      console.error('Error adding isolate:', err);
      alert('Failed to add isolate.');
    }
  };

  // Handlers: Delete Isolate
  const handleDeleteIsolate = async (isolateId) => {
    if (!window.confirm('Are you sure you want to remove this isolate and its AST susceptibility results?')) return;
    try {
      setSaveState('saving');
      await microbiologyService.deleteIsolate(isolateId);
      setActiveIsolateIdx(0);
      await loadWorkbench();
    } catch (err) {
      console.error('Error deleting isolate:', err);
      alert('Failed to remove isolate.');
    }
  };

  // Handlers: Change Isolate Organism & Auto-Populate Panel
  const handleChangeIsolateOrg = async (organismId) => {
    if (!currentIsolate) return;
    try {
      setSaveState('saving');
      await microbiologyService.saveIsolate({
        isolateID: currentIsolate.isolateID,
        cultureID: data.cultureID,
        organismID: parseInt(organismId),
        colonyMorphology: currentIsolate.colonyMorphology,
        quantityDescription: currentIsolate.quantityDescription,
        identificationMethod: currentIsolate.identificationMethod,
        remarks: currentIsolate.remarks
      });

      // Auto-populate AST panel for this organism & specimen from master
      const panelRes = await microbiologyService.getPanelForOrganismAndSpecimen(
        organismId,
        1,
        data.specimenID
      );
      if (panelRes?.data?.items?.length > 0) {
        const gridItems = panelRes.data.items.map((i, idx) => ({
          antimicrobialID: i.antimicrobialID,
          astMethodID: panelRes.data.astMethodID || 1,
          measuredValue: null,
          measuredUnit: 'mm',
          calculatedInterpretation: 'NB',
          finalInterpretation: 'NB',
          isReportable: i.reportingCategory !== 'Suppressed',
          displayOrder: idx + 1
        }));
        await microbiologyService.saveASTGrid({
          isolateID: currentIsolate.isolateID,
          results: gridItems
        });
      }

      await loadWorkbench();
    } catch (err) {
      console.error('Error updating organism:', err);
      alert('Failed to update organism.');
    }
  };

  // Handlers: Update Isolate Metadata (Morphology, Quantity, Method)
  const handleUpdateIsolateDetails = async (updatedIsolate) => {
    try {
      setSaveState('saving');
      await microbiologyService.saveIsolate({
        isolateID: updatedIsolate.isolateID,
        cultureID: data.cultureID,
        organismID: updatedIsolate.organismID,
        colonyMorphology: updatedIsolate.colonyMorphology,
        quantityDescription: updatedIsolate.quantityDescription,
        identificationMethod: updatedIsolate.identificationMethod,
        remarks: updatedIsolate.remarks
      });

      const updatedIsolates = [...data.isolates];
      updatedIsolates[activeIsolateIdx] = updatedIsolate;
      setData({ ...data, isolates: updatedIsolates });
      setSaveState('saved');
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      console.error('Error updating isolate details:', err);
      setSaveState('unsaved');
    }
  };

  // Handlers: AST Measurement Change & Live S/I/R Calculation
  const handleASTValueChange = async (drugId, value) => {
    if (!currentIsolate) return;
    const numVal = parseFloat(value);
    const existing = currentIsolate.astResults.find((r) => r.antimicrobialID === drugId);

    // Call live calculation API
    let calc = { interpretation: 'NB', ruleTrace: '' };
    if (!isNaN(numVal)) {
      try {
        const calcRes = await microbiologyService.calculateAST({
          organismID: currentIsolate.organismID,
          antimicrobialID: drugId,
          astMethodID: existing?.astMethodID || 1,
          measuredValue: numVal,
          specimenID: data.specimenID
        });
        calc = calcRes?.data || calc;
      } catch (err) {
        console.error('AST calculation API error:', err);
      }
    }

    const updatedResults = currentIsolate.astResults.map((r) => {
      if (r.antimicrobialID === drugId) {
        return {
          ...r,
          measuredValue: isNaN(numVal) ? null : numVal,
          calculatedInterpretation: calc.interpretation,
          finalInterpretation: r.isOverridden ? r.finalInterpretation : calc.interpretation,
          ruleTrace: calc.ruleTrace,
          appliedStandard: calc.appliedStandard || r.appliedStandard || 'EUCAST',
          appliedVersion: calc.appliedVersion || r.appliedVersion || 'v16.1',
          appliedBreakpointID: calc.appliedBreakpointID
        };
      }
      return r;
    });

    const updatedIsolates = [...data.isolates];
    updatedIsolates[activeIsolateIdx].astResults = updatedResults;
    setData({ ...data, isolates: updatedIsolates });

    // Save grid to server
    try {
      setSaveState('saving');
      await microbiologyService.saveASTGrid({
        isolateID: currentIsolate.isolateID,
        results: updatedResults
      });
      setSaveState('saved');
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      console.error('Error saving AST grid:', err);
      setSaveState('unsaved');
    }
  };

  // Handlers: Add Single Antibiotic to Isolate Grid
  const handleAddDrugToIsolate = async (drugId) => {
    if (!currentIsolate) return;
    const alreadyExists = currentIsolate.astResults.some((r) => r.antimicrobialID === parseInt(drugId));
    if (alreadyExists) return;

    const drug = antimicrobials.find((d) => d.antimicrobialID === parseInt(drugId));
    const newEntry = {
      antimicrobialID: parseInt(drugId),
      drugCode: drug?.drugCode || '',
      drugName: drug?.drugName || '',
      astMethodID: 1,
      measuredValue: null,
      measuredUnit: 'mm',
      calculatedInterpretation: 'NB',
      finalInterpretation: 'NB',
      isReportable: true,
      displayOrder: currentIsolate.astResults.length + 1
    };

    const newResults = [...currentIsolate.astResults, newEntry];
    try {
      setSaveState('saving');
      await microbiologyService.saveASTGrid({
        isolateID: currentIsolate.isolateID,
        results: newResults
      });
      await loadWorkbench();
    } catch (err) {
      console.error('Error adding antibiotic:', err);
      alert('Failed to add antibiotic.');
    }
  };

  // Handlers: Toggle Reportable Status
  const handleToggleReportable = async (drugId, isReportable) => {
    if (!currentIsolate) return;
    const updatedResults = currentIsolate.astResults.map((r) =>
      r.antimicrobialID === drugId ? { ...r, isReportable } : r
    );

    const updatedIsolates = [...data.isolates];
    updatedIsolates[activeIsolateIdx].astResults = updatedResults;
    setData({ ...data, isolates: updatedIsolates });

    try {
      await microbiologyService.saveASTGrid({
        isolateID: currentIsolate.isolateID,
        results: updatedResults
      });
    } catch (err) {
      console.error('Error updating reportable status:', err);
    }
  };

  // Handlers: Bulk Update AST Grid
  const handleBulkUpdateAST = async (updatedResults) => {
    if (!currentIsolate) return;
    const updatedIsolates = [...data.isolates];
    updatedIsolates[activeIsolateIdx].astResults = updatedResults;
    setData({ ...data, isolates: updatedIsolates });

    try {
      setSaveState('saving');
      await microbiologyService.saveASTGrid({
        isolateID: currentIsolate.isolateID,
        results: updatedResults
      });
      setSaveState('saved');
    } catch (err) {
      console.error('Error saving bulk AST:', err);
      setSaveState('unsaved');
    }
  };

  // Handlers: Toggle Resistance Finding
  const handleToggleResistance = async (mechanismId) => {
    if (!currentIsolate) return;
    const exists = currentIsolate.resistanceFindings.some((f) => f.mechanismID === mechanismId);
    let newFindings;
    if (exists) {
      newFindings = currentIsolate.resistanceFindings.filter((f) => f.mechanismID !== mechanismId);
    } else {
      newFindings = [
        ...currentIsolate.resistanceFindings,
        { mechanismID: mechanismId, resultState: 'Detected', testingMethod: 'Phenotypic Screen' }
      ];
    }

    try {
      setSaveState('saving');
      await microbiologyService.saveResistanceFindings({
        isolateID: currentIsolate.isolateID,
        findings: newFindings
      });
      await loadWorkbench();
    } catch (err) {
      console.error('Error updating resistance findings:', err);
      alert('Failed to update resistance findings.');
    }
  };

  // Handlers: Clinical Interpretation Override
  const handleOpenOverride = (astResult) => {
    setOverrideModal({
      isOpen: true,
      astResultId: astResult.astResultID,
      drugName: astResult.drugName,
      originalInterpretation: astResult.calculatedInterpretation,
      newInterpretation: 'R',
      reason: ''
    });
  };

  const handleSaveOverride = async () => {
    if (!overrideModal.reason || !overrideModal.reason.trim()) {
      alert('Mandatory clinical reason is required for interpretation override.');
      return;
    }
    try {
      setSaveState('saving');
      await microbiologyService.overrideASTResult({
        astResultID: overrideModal.astResultId,
        newInterpretation: overrideModal.newInterpretation,
        overrideReason: overrideModal.reason
      });
      setOverrideModal({ ...overrideModal, isOpen: false });
      await loadWorkbench();
    } catch (err) {
      console.error('Error saving override:', err);
      alert('Failed to record clinical override.');
    }
  };

  // Handlers: Remarks Save
  const handleSaveRemarks = async () => {
    try {
      setSaveState('saving');
      await microbiologyService.saveCultureStatus({
        cultureID: data.cultureID,
        cultureStatus: data.cultureStatus,
        colonyCount: data.colonyCount,
        growthSignificance: data.growthSignificance,
        isCriticalAlert: data.isCriticalAlert,
        finalRemarks: data.finalRemarks
      });
      setSaveState('saved');
      setLastSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      alert('Clinical remarks successfully saved.');
    } catch (err) {
      console.error('Error saving remarks:', err);
      setSaveState('unsaved');
      alert('Failed to save clinical remarks.');
    }
  };

  // Handlers: Release Report
  const handleReleaseReport = async (reportType) => {
    const confirm = window.confirm(
      `Are you sure you want to release this ${reportType} Microbiology Report? This action will freeze an immutable legal SHA-256 snapshot.`
    );
    if (!confirm) return;

    try {
      setSaveState('saving');
      await microbiologyService.releaseReport({
        cultureID: data.cultureID,
        reportType: reportType,
        amendmentReason: reportType === 'Amended' ? prompt('Enter reason for report amendment:') : undefined
      });
      alert(`Microbiology Report (${reportType} version) successfully authorized and released!`);
      await loadWorkbench();
    } catch (err) {
      console.error('Error releasing report:', err);
      alert('Failed to release report.');
    }
  };

  if (loading) {
    return (
      <div className="wb-loading-state">
        <div className="wb-spinner"></div>
        <p>Loading Clinical Microbiology Workbench & Master Breakpoints...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wb-error-state">
        <h3>Culture Record Not Found</h3>
        <p>Unable to find active microbiology record for Test ID: <code>{testId}</code></p>
        <button className="micro-btn-primary" onClick={() => navigate('/microbiology/worklist')}>
          &larr; Return to Worklist Queue
        </button>
      </div>
    );
  }

  return (
    <div className="wb-workstation-root">
      {/* 1. High-Density Sticky Header */}
      <WorkbenchHeader
        data={data}
        saveState={saveState}
        lastSavedTime={lastSavedTime}
        onOpenReportModal={() => setReportModalOpen(true)}
        onOpenAiModal={() => setAiModalOpen(true)}
        onOpenPatientDrawer={() => setPatientDrawerOpen(true)}
        onBackToQueue={() => navigate('/microbiology/worklist')}
      />

      {/* 2. Main Workbench Layout (Workflow Workspace + Context Sidebar) */}
      <div className="wb-workstation-body">
        <main className="wb-workstation-main">
          {/* Step-by-Step Workflow Stepper Navigation */}
          <WorkflowProgressBar
            data={data}
            activeTab={activeTab}
            onSelectTab={(tabId) => setActiveTab(tabId)}
          />

          {/* Tab 1: Direct Smear & Microscopy */}
          {activeTab === 'microscopy' && (
            <MicroscopySection
              data={data}
              onSaveMicroscopy={handleSaveMicroscopy}
              onProceedToCulture={() => setActiveTab('culture')}
              onToggleCritical={() =>
                handleSaveCultureStatus(
                  data.cultureStatus,
                  data.colonyCount,
                  data.growthSignificance,
                  !data.isCriticalAlert,
                  data.mediaPlates
                )
              }
            />
          )}

          {/* Tab 2: Culture Incubation & Colony Counts */}
          {activeTab === 'culture' && (
            <CultureSection
              data={data}
              onSaveCultureStatus={handleSaveCultureStatus}
              onProceedToOrganisms={() => setActiveTab('ast')}
              onToggleCritical={() =>
                handleSaveCultureStatus(
                  data.cultureStatus,
                  data.colonyCount,
                  data.growthSignificance,
                  !data.isCriticalAlert,
                  data.mediaPlates
                )
              }
            />
          )}

          {/* Tab 3: Organism Identification & AST Susceptibility Grid */}
          {activeTab === 'ast' && (
            <OrganismASTSection
              data={data}
              organisms={organisms}
              antimicrobials={antimicrobials}
              activeIsolateIdx={activeIsolateIdx}
              onSelectIsolate={(idx) => setActiveIsolateIdx(idx)}
              onAddIsolate={handleAddIsolate}
              onDeleteIsolate={handleDeleteIsolate}
              onChangeOrganism={handleChangeIsolateOrg}
              onUpdateIsolateDetails={handleUpdateIsolateDetails}
              onASTValueChange={handleASTValueChange}
              onAddDrugToIsolate={handleAddDrugToIsolate}
              onOpenOverride={handleOpenOverride}
              onToggleReportable={handleToggleReportable}
              onBulkUpdateAST={handleBulkUpdateAST}
            />
          )}

          {/* Tab 4: Resistance Findings & Surveillance */}
          {activeTab === 'resistance' && (
            <ResistanceExpertRulesSection
              data={data}
              activeIsolateIdx={activeIsolateIdx}
              resistanceMechanisms={resistanceMechanisms}
              onToggleResistance={handleToggleResistance}
            />
          )}

          {/* Tab 5: Master Comments & Remarks */}
          {activeTab === 'comments' && (
            <CommentsLibrarySection
              data={data}
              commentTemplates={commentTemplates}
              onChangeRemarks={(text) => setData({ ...data, finalRemarks: text })}
              onSaveRemarks={handleSaveRemarks}
            />
          )}

          {/* Tab 6: Patient Longitudinal History */}
          {activeTab === 'history' && (
            <PatientHistorySection
              history={data.previousHistory || []}
              patientID={data.patientID}
              patientName={data.patientName}
              currentOrganism={currentIsolate?.organismName}
              data={data}
            />
          )}

          {/* Tab 7: Microbiologist Review & Report Release Gate */}
          {activeTab === 'validation' && (
            <DoctorReviewModeSection
              data={data}
              onReleaseReport={handleReleaseReport}
              onOpenReportModal={() => setReportModalOpen(true)}
            />
          )}
        </main>

        {/* 3. Right-Side Context Summary Sidebar */}
        <WorkbenchSidebar
          data={data}
          activeIsolateIdx={activeIsolateIdx}
          onOpenAiModal={() => setAiModalOpen(true)}
          onOpenReportModal={() => setReportModalOpen(true)}
          onSelectTab={(tabId) => setActiveTab(tabId)}
          isCollapsed={isSnapshotCollapsed}
          onToggleCollapse={() => setIsSnapshotCollapsed((c) => !c)}
        />
      </div>

      {/* 4. Global Action & Navigation Bottom Bar */}
      <WorkbenchBottomBar
        activeTab={activeTab}
        onSelectTab={(tabId) => setActiveTab(tabId)}
        onSaveDraft={() =>
          handleSaveCultureStatus(
            data.cultureStatus,
            data.colonyCount,
            data.growthSignificance,
            data.isCriticalAlert
          )
        }
        onToggleCritical={() =>
          handleSaveCultureStatus(
            data.cultureStatus,
            data.colonyCount,
            data.growthSignificance,
            !data.isCriticalAlert
          )
        }
        onOpenReportModal={() => setReportModalOpen(true)}
        onReleaseReport={handleReleaseReport}
        isCritical={Boolean(data.isCriticalAlert)}
        isApproved={Boolean(data.approved)}
        isReadyForFinal={checkReportReadiness(data).isReadyForFinal}
        saveState={saveState}
        lastSavedTime={lastSavedTime}
        saving={saveState === 'saving'}
      />

      {/* Patient Full Details Side Drawer */}
      <PatientDetailsDrawer
        isOpen={patientDrawerOpen}
        onClose={() => setPatientDrawerOpen(false)}
        data={data}
      />

      {/* Clinical Override Modal */}
      {overrideModal.isOpen && (
        <div className="micro-modal-overlay" style={{ zIndex: 1250 }}>
          <div className="micro-modal-content" style={{ maxWidth: '520px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#0f172a' }}>
              ⚖️ Clinical Interpretation Override
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '16px' }}>
              You are manually overriding the automated breakpoint calculation for{' '}
              <strong>{overrideModal.drugName}</strong>. This override will be stamped in the legal audit log.
            </p>

            <div className="micro-form-group" style={{ marginBottom: '12px' }}>
              <label>Automated Calculated Interpretation</label>
              <input
                type="text"
                disabled
                value={overrideModal.originalInterpretation || 'NB'}
                style={{ background: '#f8fafc', fontWeight: '700' }}
              />
            </div>

            <div className="micro-form-group" style={{ marginBottom: '12px' }}>
              <label>Authorized Clinical Interpretation <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                value={overrideModal.newInterpretation}
                onChange={(e) => setOverrideModal({ ...overrideModal, newInterpretation: e.target.value })}
              >
                <option value="S">S (Susceptible)</option>
                <option value="I">I (Intermediate / Increased Exposure)</option>
                <option value="R">R (Resistant)</option>
              </select>
            </div>

            <div className="micro-form-group" style={{ marginBottom: '18px' }}>
              <label>Mandatory Clinical Justification <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea
                rows="3"
                required
                placeholder="Enter clinical rationale (e.g., In-vivo treatment failure, synergy testing, expert rule override)..."
                value={overrideModal.reason}
                onChange={(e) => setOverrideModal({ ...overrideModal, reason: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setOverrideModal({ ...overrideModal, isOpen: false })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                onClick={handleSaveOverride}
              >
                ✓ Save Clinical Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Microbiology AI Copilot Modal */}
      <MicrobiologyAIModal
        data={data}
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
      />

      {/* Official Diagnostic Report Modal with SHA-256 Tamper Verification & Print Layout */}
      <MicrobiologyReportModal
        data={data}
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
      />
    </div>
  );
}
